/**
 * Transcript Context Builder that accepts data and hit-group and build contexts for that group
 */
export default class TranscriptHitBuilder {

    /**
     * The search data
     */
    _data;

    /**
     * The hit-group (a hit instance - single or aggregated - [x, ...])
     */
    _hitGroup;

    /**
     * (approx.) allowed characters on either side of the hit term
     *
     * @type {number} (approx.) character count limit
     * @private
     */
    _hitSideContextCountLimit = 70;

    /**
     * (max.) allowed time of the context elements from the hit group
     *
     * @type {number} (max.) time limit
     * @private
     */
    _hitSideContextTimeLimit = 10;

    /**
     * Constructor
     *
     * @param data the search data
     * @param hitGroup the hit group
     */
    constructor(data, hitGroup) {
        this._data = data;
        this._hitGroup = hitGroup;
    }

    /**
     * Returns the collective word for the hit-group
     *
     * @returns {string} Collective representation of the word
     * @private
     */
    _getWord = () => {
        let combinedWord = "";
        this._hitGroup.forEach((index) => {
            const currentIndexWord = this._data[index].w;
            combinedWord += currentIndexWord + " ";
        });
        return combinedWord.trim(); // remove the unnecessary whitespace at extremities
    }

    /**
     * Returns the hit-group details
     *
     * @returns {{boundary: {start: number, end: number}, time: string, word: string}}
     * @private
     */
    _getHitGroupDetails = () => {
        return {
            "word": this._getWord(),
            "time": this._data[Math.min(...this._hitGroup)].t,
            "boundary": {
                "start": Math.min(...this._hitGroup),
                "end": Math.max(...this._hitGroup),
            }
        }
    }

    /**
     * Prepares hit data
     *
     * @returns hit information for publishing {{contextTime: string, context: string, time: string, word: string}}
     */
    prepareHitData = () => {
        const hitGroupDetails = this._getHitGroupDetails();

        let preHit = "", postHit = "";              // placeholder for pre and post hit text
        let preCharCount = 0, postCharCount = 0;    // placeholder for pre and post char counters

        // placeholder for pre and post time limits
        const maxPreTimeBoundary = this._data[hitGroupDetails.boundary.start].t - this._hitSideContextTimeLimit;    // [...10s+(<-)[start]...[end]+10s...]
        const maxPostTimeBoundary = this._data[hitGroupDetails.boundary.end].t + this._hitSideContextTimeLimit;     // [...10s+[start]...[end](->)+10s...]

        let contextStartTime = this._data[hitGroupDetails.boundary.start].t;    // fallback context start time is set to hit group's start time

        /*
         * Calculation of pre-hit context
         * subjected to (char-limit) && (context-availability) && (time-limit)
         */
        let currentIndex = hitGroupDetails.boundary.start - 1;      // [...~70chars+(<-)[start]...[end]+chars70~...]
        while(preCharCount < this._hitSideContextCountLimit && currentIndex >= 0 && maxPreTimeBoundary < this._data[currentIndex].t) {
            const currentAddition = (" " + this._data[currentIndex].w);
            preHit = currentAddition + preHit;
            contextStartTime = this._data[currentIndex].t;          // context start time

            preCharCount += currentAddition.length;
            currentIndex--;
        }
        if(currentIndex >= 0) { preHit = "..." + preHit; }           // if previous indexes available, add ellipsis

        /*
         * Calculation of post-hit context
         * subjected to (char-limit) && (context-availability) && (time-limit)
         */
        currentIndex = hitGroupDetails.boundary.end + 1;            // [...~70chars+[start]...[end](->)+chars70~...]
        while(postCharCount < this._hitSideContextCountLimit && currentIndex < this._data.length && maxPostTimeBoundary > this._data[currentIndex].t) {
            const currentAddition = (this._data[currentIndex].w + " ");
            postHit = postHit + currentAddition;

            postCharCount += currentAddition.length;
            currentIndex++;
        }
        if(currentIndex < this._data.length) { postHit += "..."; }      // if next indexes available, add ellipsis

        const currentHit = " <span>" + hitGroupDetails.word + "</span> ";     // prettify current-hit
        const context = preHit + currentHit + postHit;                      // build overall context for current-hit

        return {
            "word": hitGroupDetails.word,
            "context": context,
            "time": hitGroupDetails.time,
            "contextTime": contextStartTime,
        }
    }
}